import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaDownload, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFileAlt, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';

const Downloads = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const documents = [
    {
      id: 1,
      name: 'School Calendar 2024',
      description: 'Complete academic calendar with all important dates, holidays, and events for the 2024 school year.',
      type: 'PDF',
      size: '2.3 MB',
      category: 'Academic',
      date: '2024-01-15',
      downloads: 245,
      icon: FaFilePdf,
      color: 'red',
      fileUrl: '/downloads/school-calendar-2024.pdf'
    },
    {
      id: 2,
      name: 'Student Handbook',
      description: 'Comprehensive guide containing school policies, rules, and guidelines for all students.',
      type: 'PDF',
      size: '1.8 MB',
      category: 'General',
      date: '2024-01-10',
      downloads: 189,
      icon: FaFilePdf,
      color: 'red',
      fileUrl: '/downloads/student-handbook.pdf'
    },
    {
      id: 3,
      name: 'Parent Information Guide',
      description: 'Essential information for parents including contact details, policies, and important procedures.',
      type: 'Word',
      size: '3.1 MB',
      category: 'Parent',
      date: '2024-01-08',
      downloads: 156,
      icon: FaFileWord,
      color: 'blue',
      fileUrl: '/downloads/parent-information-guide.docx'
    },
    {
      id: 4,
      name: 'Academic Performance Report Template',
      description: 'Template for teachers to create standardized academic performance reports.',
      type: 'Excel',
      size: '456 KB',
      category: 'Academic',
      date: '2024-01-05',
      downloads: 98,
      icon: FaFileExcel,
      color: 'green',
      fileUrl: '/downloads/academic-report-template.xlsx'
    },
    {
      id: 5,
      name: 'School Policies and Procedures',
      description: 'Complete documentation of all school policies, procedures, and administrative guidelines.',
      type: 'PDF',
      size: '4.2 MB',
      category: 'General',
      date: '2024-01-03',
      downloads: 134,
      icon: FaFilePdf,
      color: 'red',
      fileUrl: '/downloads/school-policies.pdf'
    },
    {
      id: 6,
      name: 'Extracurricular Activities Form',
      description: 'Application form for students interested in participating in extracurricular activities.',
      type: 'Word',
      size: '1.2 MB',
      category: 'Student',
      date: '2024-01-02',
      downloads: 203,
      icon: FaFileWord,
      color: 'blue',
      fileUrl: '/downloads/extracurricular-form.docx'
    },
    {
      id: 7,
      name: 'School Newsletter Template',
      description: 'Template for creating school newsletters and announcements.',
      type: 'PowerPoint',
      size: '2.8 MB',
      category: 'General',
      date: '2024-01-01',
      downloads: 67,
      icon: FaFilePowerpoint,
      color: 'orange',
      fileUrl: '/downloads/newsletter-template.pptx'
    },
    {
      id: 8,
      name: 'Science Fair Guidelines',
      description: 'Detailed guidelines and requirements for the annual science fair competition.',
      type: 'PDF',
      size: '1.5 MB',
      category: 'Academic',
      date: '2023-12-28',
      downloads: 178,
      icon: FaFilePdf,
      color: 'red',
      fileUrl: '/downloads/science-fair-guidelines.pdf'
    },
    {
      id: 9,
      name: 'Sports Team Registration',
      description: 'Registration form for students interested in joining school sports teams.',
      type: 'Word',
      size: '890 KB',
      category: 'Sports',
      date: '2023-12-25',
      downloads: 145,
      icon: FaFileWord,
      color: 'blue',
      fileUrl: '/downloads/sports-registration.docx'
    },
    {
      id: 10,
      name: 'School Logo and Branding',
      description: 'High-resolution school logo and branding materials for official use.',
      type: 'Image',
      size: '5.6 MB',
      category: 'General',
      date: '2023-12-20',
      downloads: 89,
      icon: FaFileImage,
      color: 'purple',
      fileUrl: '/downloads/school-logo.zip'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Documents', color: 'gray' },
    { id: 'academic', name: 'Academic', color: 'blue' },
    { id: 'general', name: 'General', color: 'green' },
    { id: 'parent', name: 'Parent', color: 'purple' },
    { id: 'student', name: 'Student', color: 'orange' },
    { id: 'sports', name: 'Sports', color: 'red' }
  ];

  const getFileTypeIcon = (type) => {
    const icons = {
      'PDF': FaFilePdf,
      'Word': FaFileWord,
      'Excel': FaFileExcel,
      'PowerPoint': FaFilePowerpoint,
      'Image': FaFileImage
    };
    return icons[type] || FaFileAlt;
  };

  const getColorClasses = (color) => {
    const colors = {
      red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    };
    return colors[color] || colors.blue;
  };

  const getIconColor = (color) => {
    const colors = {
      red: 'text-red-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      orange: 'text-orange-600',
      purple: 'text-purple-600'
    };
    return colors[color] || colors.blue;
  };

  const filteredAndSortedDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          doc.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || doc.category.toLowerCase() === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'size':
          aValue = parseFloat(a.size.replace(' MB', '').replace(' KB', ''));
          bValue = parseFloat(b.size.replace(' MB', '').replace(' KB', ''));
          break;
        case 'downloads':
          aValue = a.downloads;
          bValue = b.downloads;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleDownload = (fileUrl, fileName) => {
    // In a real app, this would trigger the actual download
    console.log(`Downloading ${fileName} from ${fileUrl}`);
    // For demo purposes, we'll just show an alert
    alert(`Download started for ${fileName}`);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Helmet>
        <title>Rasoha Academy | Downloads - School Documents & Resources</title>
        <meta name="description" content="Download important school documents, forms, and resources from Rasoha Academy. Access academic calendars, policies, forms, and educational materials." />
        <meta name="keywords" content="Rasoha Academy Downloads, School Documents, Academic Calendar, School Forms, Educational Resources, School Policies" />
        <meta property="og:title" content="Rasoha Academy | Downloads - School Documents & Resources" />
        <meta property="og:description" content="Download important school documents, forms, and resources from Rasoha Academy." />
        <meta property="og:url" content="https://rasohaacademy.com/downloads" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Downloads
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
            Access important documents, forms, and resources for students, parents, and staff
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between" data-aos="fade-up">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sort Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8" data-aos="fade-up">
            <div className="mb-4 sm:mb-0">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Available Documents
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Showing {filteredAndSortedDocuments.length} of {documents.length} documents
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 dark:text-gray-300">Sort by:</span>
              <div className="flex space-x-2">
                {[
                  { field: 'name', label: 'Name' },
                  { field: 'date', label: 'Date' },
                  { field: 'size', label: 'Size' },
                  { field: 'downloads', label: 'Downloads' }
                ].map(({ field, label }) => (
                  <button
                    key={field}
                    onClick={() => toggleSort(field)}
                    className={`flex items-center px-3 py-1 rounded text-sm font-medium transition-colors duration-200 ${
                      sortBy === field
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label}
                    {sortBy === field && (
                      sortOrder === 'asc' ? <FaSortAmountUp className="ml-1" /> : <FaSortAmountDown className="ml-1" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Documents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedDocuments.map((doc, index) => {
              const FileIcon = getFileTypeIcon(doc.type);
              return (
                <div
                  key={doc.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                  data-aos="zoom-in"
                  data-aos-delay={index * 100}
                >
                  <div className="p-6">
                    {/* File Icon and Type */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className={`w-12 h-12 ${getColorClasses(doc.color).split(' ')[0]} rounded-lg flex items-center justify-center mr-4`}>
                          <FileIcon className={`w-6 h-6 ${getIconColor(doc.color)}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                            {doc.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {doc.type} • {doc.size}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm leading-relaxed">
                      {doc.description}
                    </p>

                    {/* Category and Downloads */}
                    <div className="flex items-center justify-between mb-4">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getColorClasses(doc.color)}`}>
                        {doc.category}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {doc.downloads} downloads
                      </span>
                    </div>

                    {/* Download Button */}
                    <button
                      onClick={() => handleDownload(doc.fileUrl, doc.name)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center"
                    >
                      <FaDownload className="w-4 h-4 mr-2" />
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredAndSortedDocuments.length === 0 && (
            <div className="text-center py-12" data-aos="fade-up">
              <FaFileAlt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No documents found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Try adjusting your search terms or filters.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Something Else?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Can't find what you're looking for? Contact us for additional documents or resources.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              Request Document
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105">
              Contact Support
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Downloads; 